import {
  FileText,
  FileSpreadsheet,
  Presentation,
  FileArchive,
  FileImage,
  File as FileIcon,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeTone } from '@/shared/components/ui/Badge';

export type FileKind = 'pdf' | 'doc' | 'sheet' | 'slides' | 'text' | 'archive' | 'image' | 'unknown';

export interface FileTypeMeta {
  ext: string;
  kind: FileKind;
  label: string;
  icon: LucideIcon;
  tone: BadgeTone;
}

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];

/** Strip query/hash and return the last path segment as a filename. */
export function deriveFileName(url?: string): string {
  if (!url) return '';
  const clean = url.split('#')[0].split('?')[0];
  const seg = clean.split('/').pop() || '';
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

function extOf(url?: string): string {
  const name = deriveFileName(url);
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Derive the file type ONLY from the URL extension (no backend dependency). */
export function detectFileType(url?: string): FileTypeMeta {
  const ext = extOf(url);
  if (ext === 'pdf') return { ext, kind: 'pdf', label: 'PDF', icon: FileText, tone: 'danger' };
  if (ext === 'doc' || ext === 'docx') return { ext, kind: 'doc', label: ext.toUpperCase(), icon: FileText, tone: 'info' };
  if (ext === 'xls' || ext === 'xlsx') return { ext, kind: 'sheet', label: ext.toUpperCase(), icon: FileSpreadsheet, tone: 'success' };
  if (ext === 'ppt' || ext === 'pptx') return { ext, kind: 'slides', label: ext.toUpperCase(), icon: Presentation, tone: 'warning' };
  if (ext === 'txt') return { ext, kind: 'text', label: 'TXT', icon: FileText, tone: 'neutral' };
  if (ext === 'zip' || ext === 'rar') return { ext, kind: 'archive', label: ext.toUpperCase(), icon: FileArchive, tone: 'neutral' };
  if (IMAGE_EXTS.includes(ext)) return { ext, kind: 'image', label: ext.toUpperCase(), icon: FileImage, tone: 'primary' };
  return { ext: ext || '', kind: 'unknown', label: ext ? ext.toUpperCase() : 'LINK', icon: FileIcon, tone: 'neutral' };
}

export function isImage(url?: string): boolean {
  return detectFileType(url).kind === 'image';
}

export function isPdf(url?: string): boolean {
  return detectFileType(url).kind === 'pdf';
}

/** Preview is supported only for images and PDFs. */
export function canPreview(url?: string): boolean {
  return !!url && (isImage(url) || isPdf(url));
}

/**
 * Resolves a document URL (relative like `/uploads/...` or full URL) into an
 * accessible absolute URL pointing to the API server or static host.
 */
export function resolveSalesDocUrl(url?: string): string {
  if (!url) return '';
  if (/^(?:https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:4001').replace(/\/+$/, '');
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${apiBase}${cleanPath}`;
}

